import {doPOST, refreshKeycloakToken, shopRestBackendBaseUrl} from './mydummen.fetch.service';

const ONE_MINUTE_IN_MILLISECONDS = 60000;
const CUSTOMER_REST_BASE_URL = `${shopRestBackendBaseUrl}/secured/app/customers`;

/**
 * Determines if the (for example) browser is not minimized or otherwise inactive.
 * @see for more info: https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilityState
 */
const isPageVisible = () => {
	try {
		return document.visibilityState === 'visible';
	} catch (e) {
		// For browsers which don't support this, simply assume the page is visible/active.
		return true;
	}
};

/**
 * Sends a 'Keep customer active/alive' request.
 * See CustomerController.updateActiveState() for more information.
 */
const sendCustomerKeepAliveRequest = async (): Promise<void> => {
	try {
		await doPOST(`${CUSTOMER_REST_BASE_URL}/keep-active`);
	} catch (e) {
		// Something failed while keeping the customers state active.
		// Ignore (but do log it in the console) because the next interval may succeed.
		console.warn(e);
	}
};

/**
 * In the regional website / my-dummen this POST request is fired every X time.
 * So even when the customer is not requesting other catalog endpoints,
 * the customer will still be considered active.
 *
 * This interval is set low (5 minutes), because it is also directly triggered
 * and the customer is considered active, when calling the catalog (or this backend call) within every 15 minutes.
 */
const initCustomerKeepAlive = async () => {
	setInterval(() => {
		// Don't send keep-alive if the page / browser-tab is not active (e.g. minimized)
		// to prevent the customer stays active because he doesn't close his browser tab for example
		// and keeps it open for the entire day or longer.
		if (!isPageVisible()) {
			return;
		}

		sendCustomerKeepAliveRequest();

	}, ONE_MINUTE_IN_MILLISECONDS * 5);

	// Also directly trigger the logic, so that on a page change/refresh on the regional website,
	// (without calling the catalog backend) the 'customer is active' state will be updated.
	await sendCustomerKeepAliveRequest();
};

/**
 * This logic makes sure the KeyCloak access token is refreshed (if needed).
 *
 * @see keycloak-<env>.json for the 'token-minimum-time-to-live' setting,
 * which is the amount of time the token should be  minimally valid for when checking if
 * the token should be refreshed. This amount of seconds should be a bit higher than the interval.
 */
const initKeepKeycloakTokenUpToDate = async () => {
	let refreshTokenBusy = false;

	setInterval(async () => {
		// If still busy (or error occurred, don't keep spamming the server)
		if (refreshTokenBusy) {
			return;
		}

		refreshTokenBusy = true;

		await refreshKeycloakToken();

		refreshTokenBusy = false;

	}, ONE_MINUTE_IN_MILLISECONDS);
};

export const initCustomerLoggedInIntervals = async () => {
	await initCustomerKeepAlive();
	await initKeepKeycloakTokenUpToDate();
};
